/**
 * Resi/AWB format validator — mirrors backend `_validate_resi` in server.py.
 * Both sides validate so the user gets instant feedback while typing AND the
 * backend stays a defense-in-depth safety net.
 *
 * Returns `{ hardError: string | null, normalized: string }`.
 *   - hardError → human-readable rejection message; if non-null, the field is
 *     invalid and submit must be disabled.
 *   - normalized → the AWB stripped to A-Z0-9 + uppercased, suitable for
 *     comparison / logging.
 *
 * Soft hints from earlier iteration are removed — pattern mismatch is now a
 * hard reject per user spec (Feb 2026).
 */

const _NONSENSE_LITERALS = new Set([
  'test', 'test1234', 'asdf1234', 'abcd1234', '12345678',
  'abcdefgh', '123456789', '1234567890',
]);

function isObviousNonsense(raw) {
  const s = (raw || '').trim();
  if (!s) return true;
  const norm = s.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (norm.length < 8 || norm.length > 24) return true;
  if (/[^A-Za-z0-9]/.test(s)) return true;          // spaces / symbols
  if (new Set(norm).size === 1) return true;        // all same char
  if (_NONSENSE_LITERALS.has(norm.toLowerCase())) return true;
  if (/^(?:test|fake|dummy)/i.test(norm)) return true;
  // Home-row / keyboard-walk prefix (asd, qwe, zxc, asdf, qwer, zxcv) →
  // almost-always lazy typing. Mirrors backend _resi_is_nonsense.
  if (/^(?:ASDF?|QWER?|ZXCV?)/.test(norm)) return true;
  // 8+ run of ascending sequential digits anywhere
  if (norm.includes('12345678') || norm.includes('23456789') || norm.includes('34567890')) return true;
  // 8+ run of descending sequential digits anywhere
  if (norm.includes('87654321') || norm.includes('98765432') || norm.includes('09876543')) return true;
  return false;
}

function courierKey(courierName) {
  const c = (courierName || '').toLowerCase().replace(/[&.\- ]/g, '');
  if (c.includes('jne')) return 'jne';
  if (c.includes('jnt') || c.startsWith('jt')) return 'jnt';
  if (c.includes('sicepat')) return 'sicepat';
  if (c.includes('anteraja')) return 'anteraja';
  if (c.includes('idexpress') || c.includes('idexp')) return 'idexpress';
  if (c.includes('pos') && c.includes('indo')) return 'pos';
  if (c === 'pos') return 'pos';
  if (c.includes('ninja')) return 'ninja';
  if (c.includes('lion')) return 'lion';
  if (c.includes('tiki')) return 'tiki';
  return 'other';
}

const RESI_PATTERNS = {
  jne:       /^[A-Z0-9]{11,16}$/,
  jnt:       /^[A-Z0-9]{10,16}$/,
  sicepat:   /^[A-Z0-9]{10,16}$/,
  anteraja:  /^[0-9]{12,15}$/,
  idexpress: /^[A-Z0-9]{10,16}$/,
  pos:       /^[A-Z0-9]{11,16}$/,    // loosened Feb 2026 — Pos has LP/CP/RR prefixes
  ninja:     /^[A-Z0-9]{10,16}$/,
  lion:      /^[A-Z0-9]{12,14}$/,    // loosened Feb 2026 — some tiers use letters
  tiki:      /^[0-9]{12,16}$/,        // loosened Feb 2026 — ECO 12, REG 14, etc.
};

/**
 * @param {string} courier  courier name (free-text user input)
 * @param {string} tracking raw AWB string as typed
 * @returns {{ hardError: string|null, errorKind: 'nonsense'|'pattern'|null, normalized: string }}
 */
export function validateResi(courier, tracking) {
  const raw = (tracking || '').trim();
  const normalized = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

  if (!raw) {
    return { hardError: 'Please enter the tracking number (resi / AWB).', errorKind: 'nonsense', normalized };
  }
  if (isObviousNonsense(raw)) {
    return {
      hardError: "That tracking number doesn't look valid. Please double-check the resi on your courier receipt.",
      errorKind: 'nonsense',
      normalized,
    };
  }
  const key = courierKey(courier);
  const pat = RESI_PATTERNS[key];
  if (pat && !pat.test(normalized)) {
    const courierLabel = (courier || '').trim() || 'this courier';
    return {
      hardError: `This doesn't look like a typical ${courierLabel} tracking number. Please double-check the resi on your courier receipt.`,
      errorKind: 'pattern',
      normalized,
    };
  }
  return { hardError: null, errorKind: null, normalized };
}
